import { z } from 'zod';
import { CHIPS, POSITIONS } from './rules.ts';

/**
 * Zod validators for the enums in rules.ts. Named `*Schema` so the plain
 * `Position` / `Chip` TypeScript types stay importable without ambiguity.
 */
export const PositionSchema = z.enum(POSITIONS);
export const ChipSchema = z.enum(CHIPS);

/**
 * A player as the user gives them to us. Deliberately small: everything else
 * (form, fixtures, ownership) is joined from ingested FPL data at analysis time,
 * so an upload stays valid even as the season moves on.
 */
export const SquadPlayer = z.object({
  /** FPL element id when known — lets us join to ingested data exactly. */
  fplId: z.number().int().positive().optional(),
  name: z.string().min(1).max(80),
  position: PositionSchema,
  /** Club short name, e.g. "ARS". Used for the max-3-per-club rule. */
  club: z.string().min(2).max(4).toUpperCase(),
  /** Purchase price in tenths of a million. */
  price: z.number().int().min(35).max(200),
  /** Selling price if it differs from purchase price. */
  sellingPrice: z.number().int().min(35).max(200).optional(),
  isStarting: z.boolean().default(false),
  isCaptain: z.boolean().default(false),
  isViceCaptain: z.boolean().default(false),
  /** 1-3 for bench order; ignored for starters. */
  benchOrder: z.number().int().min(1).max(4).optional(),
});
export type SquadPlayer = z.infer<typeof SquadPlayer>;

/** One chip played, and the gameweek it was played in — FPL's own chip
 * history, which `computeChipsAvailable` deliberately throws away because it
 * only needs the count. */
export const ChipUse = z.object({
  chip: ChipSchema,
  gameweek: z.number().int().min(1).max(38),
});
export type ChipUse = z.infer<typeof ChipUse>;

export const SquadInput = z.object({
  /** Optional label so a user can keep several drafts apart. */
  name: z.string().min(1).max(60).default('My squad'),
  /** FPL entry id, when the squad was imported rather than typed in. */
  entryId: z.number().int().positive().optional(),
  gameweek: z.number().int().min(1).max(38),
  players: z.array(SquadPlayer).length(15),
  /** Money not spent, in tenths of a million. */
  bank: z.number().int().min(0).max(1000).default(0),
  freeTransfers: z.number().int().min(0).max(5).default(1),
  chipsAvailable: z.array(ChipSchema).default([...CHIPS]),
  /** The chip FPL reports as active for this gameweek, if any. Optional
   * rather than defaulted: a default would make this field required in
   * `z.infer` output, breaking every existing squad fixture that doesn't
   * set it. */
  activeChip: ChipSchema.optional(),
  /** Chips already played this season, and the gameweek each was played in.
   * Optional, not defaulted, same reasoning as `activeChip`: a default would
   * make this required in `z.infer`, breaking every existing squad fixture
   * that doesn't set it. */
  chipsUsed: z.array(ChipUse).optional(),
});
export type SquadInput = z.infer<typeof SquadInput>;

export const Squad = SquadInput.extend({
  id: z.string(),
  /** Owning user id. Set only from the verified bearer token — never from
   * client-submitted input, which is why it lives here and not on SquadInput. */
  userId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Squad = z.infer<typeof Squad>;

export const RuleViolation = z.object({
  code: z.enum([
    'SQUAD_SIZE',
    'POSITION_COUNT',
    'CLUB_LIMIT',
    'BUDGET',
    'XI_SIZE',
    'FORMATION',
    'CAPTAIN',
  ]),
  message: z.string(),
});
export type RuleViolation = z.infer<typeof RuleViolation>;

export const SquadValidation = z.object({
  valid: z.boolean(),
  violations: z.array(RuleViolation),
  /** Total squad value in tenths, including the bank. */
  totalValue: z.number(),
});
export type SquadValidation = z.infer<typeof SquadValidation>;
